import { CloudUploadOutlined } from '@ant-design/icons';
import Papa from 'papaparse';
import React, { type Dispatch, type SetStateAction, useRef } from 'react';
import styled, { css } from 'styled-components';
import * as XLSX from 'xlsx';

import { Space, VFlex } from '@/components/base';
import Button from '@/components/Button/Button';
import Typo from '@/components/Typography/Typo';
import useColor from '@/hooks/domain/useColor';
import { fromWindows1252, nullCheckToString } from '@/utils/util';

type Props = {
  uploadData: (data: any, fields: string[]) => void;
  dragActive: boolean;
  setDragActive: Dispatch<SetStateAction<boolean>>;
};

const hasUtf8Bom = (bytes: Uint8Array) => {
  if (bytes[0] === 0xef && bytes[1] === 0xbb && bytes[2] === 0xbf) {
    return true;
  }

  return false;
};

const decodeTextFile = async (file: File) => {
  const bytes = new Uint8Array(await file.arrayBuffer());

  if (hasUtf8Bom(bytes)) {
    return new TextDecoder('utf-8').decode(bytes);
  }

  try {
    return new TextDecoder('utf-8', { fatal: true }).decode(bytes);
  } catch (error) {
    console.warn('Failed to decode CSV as utf-8, falling back to euc-kr', {
      error,
    });

    return new TextDecoder('euc-kr').decode(bytes);
  }
};

const DnDFileUploader = (props: Props) => {
  const color = useColor();

  const inputRef = useRef<HTMLInputElement>(null);

  const handleDrag = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      props.setDragActive(true);
    } else if (e.type === 'dragleave') {
      props.setDragActive(false);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setTimeout(() => {
      props.setDragActive(false);
    }, 100);

    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFile(e.dataTransfer.files[0]);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.target.files && e.target.files[0]) {
      handleFile(e.target.files[0]);
    }
  };

  const handleFile = async (file: File) => {
    const fileName = file.name;
    const splits = fileName.split('.');
    const fileNameExtension = splits[splits.length - 1];

    if (
      !(
        fileNameExtension === 'csv' ||
        fileNameExtension === 'xlsx' ||
        fileNameExtension === 'json'
      )
    ) {
      throw new Error('Invalid File Name Extension');
    }

    switch (fileNameExtension) {
      case 'xlsx':
        parseXlsx(file, false);
        break;
      case 'csv':
        await parseCsv(file);
        break;
      case 'json':
        parseJson(file);
        break;
    }
  };

  const onButtonClick = () => {
    if (inputRef.current) inputRef.current.click();
  };

  function excelDateToJSDate(serial: number): Date {
    const epoch = new Date(1899, 11, 30); // 엑셀에서 사용하는 1900년 1월 1일 전의 기준 날짜
    const millisecondsInADay = 24 * 60 * 60 * 1000;
    return new Date(epoch.getTime() + serial * millisecondsInADay);
  }

  const parseJson = (file: File) => {
    const reader = new FileReader();
    reader.onload = (event) => {
      const string = event.target?.result as string;
      const data = JSON.parse(string);
      props.uploadData(data, Object.keys(data[0]));
    };
    reader.readAsText(file);
  };

  const parseXlsx = (file: File, isWindows1252: boolean) => {
    const reader = new FileReader();

    reader.onload = (event) => {
      const arrayBuffer = event.target?.result as ArrayBuffer;
      const workbook = XLSX.read(new Uint8Array(arrayBuffer), {
        type: 'array',
      });
      const sheetName = workbook.SheetNames[0];
      const worksheet = workbook.Sheets[sheetName];

      const rows: any[][] = XLSX.utils.sheet_to_json(worksheet, { header: 1 });

      if (rows.length === 0) {
        console.error('Excel file is empty or invalid.');
        return;
      }

      const fileFields = rows[0] as string[];
      const mappedData = rows
        .slice(1)
        .map((row) => {
          if (row.length == 0) {
            return;
          }
          const obj: Record<string, string> = {};
          fileFields.forEach((field, index) => {
            let value = nullCheckToString(row[index]);
            if (isWindows1252) {
              value = fromWindows1252(value);
            }

            if (!isNaN(value)) {
              if (field.toLowerCase().includes('date')) {
                value = excelDateToJSDate(Number(value));
              }
            }

            obj[field] = value;
          });
          return obj;
        })
        .filter((d) => d);

      props.uploadData(mappedData, fileFields);
    };

    reader.onerror = (error) => {
      console.error('Error reading the file:', error);
    };

    reader.readAsArrayBuffer(file);
  };

  const parseCsv = async (file: File) => {
    const text = await decodeTextFile(file);

    Papa.parse(text, {
      header: true,
      skipEmptyLines: true,
      complete: (results) => {
        props.uploadData(results.data, results.meta.fields as string[]);
      },
    });
  };

  return (
    <Wrapper
      id="form-file-upload"
      onDragEnter={handleDrag}
      onDragLeave={handleDrag}
      onDragOver={handleDrag}
      onDrop={handleDrop}
      onSubmit={(e) => e.preventDefault()}
      dragActive={props.dragActive}
    >
      <input
        ref={inputRef}
        type="file"
        className="input-file-upload"
        onChange={handleChange}
      />
      <label className="label-file-upload">
        <VFlex a="center" style={{ textAlign: 'center' }}>
          <CloudUploadOutlined
            style={{ fontSize: '124px', color: color.gray500 }}
          />
          <Space h="16px" />
          <Typo type="body1" weight="sb" color={color.gray700}>
            Drag & Drop to Upload File
          </Typo>
          <Space h="4px" />
        </VFlex>
        <Typo type="body1" weight="r" color={color.gray600}>
          or
        </Typo>
        <Button sizeType="l" onClick={onButtonClick}>
          Browse File
        </Button>
      </label>
    </Wrapper>
  );
};

const Wrapper = styled.form<{ dragActive: boolean }>`
  width: 100%;
  height: 300px;
  flex-shrink: 0;
  .input-file-upload {
    display: none;
  }

  .label-file-upload {
    display: flex;
    justify-content: center;
    align-items: center;
    flex-direction: row;
    width: 100%;
    height: 100%;
    background: ${(props) => props.theme.colors.gray100};
    border: 2px dashed ${(props) => props.theme.colors.gray500};
    border-radius: 4px;
    gap: 16px;
    transition: all 0.2s;
    ${({ dragActive }) =>
      dragActive &&
      css`
        background: ${(props) => props.theme.colors.primaryAlpha(0.1)};
      `}
  }
  .upload-button {
    display: flex;
    justify-content: center;
    align-items: center;
    width: 142px;
    height: 42px;
    background: ${(props) => props.theme.colors.gray100};
    border: 1px solid ${(props) => props.theme.colors.primary100};
    border-radius: 4px;

    font-weight: 600;
    font-size: 14px;
    line-height: 20px;
    color: ${(props) => props.theme.colors.primary100};
  }
`;

export default DnDFileUploader;
